import TitrePage from "../components/charte/TitrePage";
import React, { useCallback, useEffect, useState } from "react";
import locationApi from "../utilities/locationApi";
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle,
  MenuItem, Select, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";
import UnpublishedRoundedIcon from "@mui/icons-material/UnpublishedRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ExploreRoundedIcon from "@mui/icons-material/ExploreRounded";
import ListRoundedIcon from "@mui/icons-material/ListRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loaderOff, loaderOn, logout, openSnackbar } from "../redux/actions";
import store from "../redux/store";
import { cloturerQuete, publierQuete, supprimerQuete } from "../utilities/locationService";
import { messageServeur } from "../utilities/userService";
import { estArchivee, texteConfirmationPublication } from "../utilities/publicationQuete";
import { TonStatut, statutPaiement, statutQuete } from "../utilities/statutsQuete";
import { STYLE_TON } from "../theme/tribeTheme";
import { AIDE_QUETES } from "../aide/textesAide";

// Adresse de location-service, la même que celle du client unique (Z2-38) :
// relative en développement (proxy), celle du service en production.
const URL_LOCATION = locationApi.defaults.baseURL ?? "";

/* ═══════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════ */
/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg:           "var(--c-bg)",
  card:         "var(--c-surface)",
  border:       "var(--c-border)",
  borderInput:  "var(--c-border)",
  accent:       "#1F1E1B",
  accentSoft:   "#57534A",
  accentBg:     "rgba(31,30,27,0.07)",
  accentBorder: "rgba(31,30,27,0.45)",
  text:         "#1F1E1B",
  sub:          "#57534A",
  label:        "#57534A",
  muted:        "#57534A",
  success:      "#356A0F",
  successBg:    "rgba(53,106,15,0.10)",
  successBdr:   "rgba(53,106,15,0.45)",
  gold:         "#7C5600",
  goldBg:       "rgba(124,86,0,0.10)",
  goldBdr:      "rgba(124,86,0,0.45)",
  error:        "#A23A0D",
  errorBg:      "rgba(162,58,13,0.08)",
  errorBdr:     "rgba(162,58,13,0.45)",
  row:          "rgba(31,30,27,0.03)",
  rowHover:     "rgba(31,30,27,0.06)",
  childRow:     "rgba(31,30,27,0.02)",
};

/* ═══════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════ */
interface Quest {
  id?: string;
  _id?: string;
  titre: string;
  types: string[];
  statut: string;
  createdAt?: string;
  dateDebut?: string;
  challengeMode?: boolean;
  statutPaiement?: string;
  tokensBase?: number | null;
  budgetClient?: number | null;
  source?: string;
  createdBy?: string;
  frequenceCycle?: string | null;
  parentQuestId?: string | null;
  latMin?: number;
  latMax?: number;
  lngMin?: number;
  lngMax?: number;
  zonePolygon?: unknown[];
}

/**
 * Une quête sans zone n'est pas seulement incomplète : le géorepérage de
 * POST /quest/{id}/submit ne s'active que si la bbox existe, donc un Spotter
 * peut y soumettre depuis n'importe où. Le marqueur rend ce stock visible
 * dans la file — le nom de zone demandé, lui, n'apparaît qu'à l'édition.
 */
function sansGeographie(q: Quest): boolean {
  const rectangle = q.latMin != null && q.latMax != null && q.lngMin != null && q.lngMax != null;
  const polygone  = Array.isArray(q.zonePolygon) && q.zonePolygon.length >= 3;
  return !rectangle && !polygone;
}

interface SpringPage {
  content: Quest[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/* ═══════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════ */
const QUEST_TYPE_LABELS: Record<string, string> = {
  poi:            "Collecte de lieux",
  prix:           "Relevé de prix",
  photo:          "Photo terrain",
  enquete:        "Enquête",
  verification:   "Vérification de lieux",
  infrastructure: "Infrastructure",
  signal:         "Signal réseau",
  sante:          "Données sanitaires",
  agricole:       "Données agricoles",
  commerce:       "Recensement commerce",
};

const QUEST_TYPE_EMOJIS: Record<string, string> = {
  poi: "📍", prix: "🏷", photo: "📸", enquete: "📋",
  verification: "✅", infrastructure: "🏗", signal: "📶",
  sante: "🏥", agricole: "🌾", commerce: "🏪",
};

interface StatusStyle { label: string; bg: string; border: string; color: string; }

/* Libellés communs à la liste et au détail (utilities/statutsQuete) ; seules
   les couleurs sont propres à la page. */
const COULEURS: Record<TonStatut, Omit<StatusStyle, "label">> = STYLE_TON;

function statusStyle(s: string): StatusStyle {
  const { libelle, ton } = statutQuete(s);
  return { label: libelle, ...COULEURS[ton] };
}

function paiementStyle(sp?: string): StatusStyle {
  const { libelle, ton } = statutPaiement(sp, "En attente");
  return { label: libelle, ...COULEURS[ton] };
}

function fmtDate(d?: string): string {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

function getId(q: Quest): string {
  return q.id ?? q._id ?? "";
}

function isPublishedStatus(statut: string): boolean {
  return ["publiee", "published", "active"].includes(statut?.toLowerCase());
}

function isRecurringParent(q: Quest): boolean {
  return !!q.frequenceCycle && !q.parentQuestId;
}

const PAGE_SIZE = 50;

/* ═══════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════ */
/* sourceInitiale : « client_ia » ouvre la liste sur les demandes clients
   (Quêtes › Demandes clients) ; même filtre et même appel que le bouton. */
export default function Quests({ sourceInitiale = "all" }: { sourceInitiale?: "all" | "tribe" | "client_ia" } = {}) {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const isLoggedIn     = useSelector((state: any) => state.auth.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth.isTokenExpired);

  /* ── List state ── */
  const [quests,        setQuests]        = useState<Quest[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage,   setCurrentPage]   = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [loadingMore,   setLoadingMore]   = useState(false);

  /* ── Filters ── */
  const [sourceFilter, setSourceFilter] = useState<"all" | "tribe" | "client_ia">(sourceInitiale);
  useEffect(() => { setSourceFilter(sourceInitiale); }, [sourceInitiale]);
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [clientList,   setClientList]   = useState<string[]>([]);

  /* ── Row actions ── */
  const [publishing,   setPublishing]   = useState<string | null>(null);
  const [unpublishing, setUnpublishing] = useState<string | null>(null);
  const [deleting,     setDeleting]     = useState<string | null>(null);
  const [cloturant,    setCloturant]    = useState<string | null>(null);
  const [selected,     setSelected]     = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  /* ── Tree expansion ── */
  const [expanded,        setExpanded]        = useState<Record<string, Quest[]>>({});
  const [expandedOpen,    setExpandedOpen]    = useState<Record<string, boolean>>({});
  const [cyclesLoading,   setCyclesLoading]   = useState<Record<string, boolean>>({});
  const [cyclesError,     setCyclesError]     = useState<Record<string, boolean>>({});

  /* ── Confirmation dialog ── */
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; body: string; onConfirm: () => void;
  }>({ open: false, title: "", body: "", onConfirm: () => {} });

  const authHeader = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}`,
  });


  /* ── Fetch clients list ── */
  useEffect(() => {
    const token = sessionStorage.getItem("jwtToken") ?? "";
    fetch(`${URL_LOCATION}/quest/clients`, {
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: string[]) => setClientList(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  /* ── Fetch quests (paginated) ── */
  const fetchQuests = useCallback(async (page: number, append: boolean) => {
    append ? setLoadingMore(true) : setLoading(true);

    const params = new URLSearchParams({
      page:      String(page),
      size:      String(PAGE_SIZE),
      rootsOnly: "true",
    });
    if (sourceFilter !== "all") params.set("source", sourceFilter);
    if (clientFilter !== "all") params.set("createdBy", clientFilter);

    const url   = `${URL_LOCATION}/quest/all?${params}`;
    const token = sessionStorage.getItem("jwtToken") ?? "";

    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${body ? ` — ${body}` : ""}`);
      }
      const data: SpringPage = await res.json();
      const content = Array.isArray(data.content) ? data.content : [];
      setTotalElements(data.totalElements ?? 0);
      setCurrentPage(page);
      if (append) {
        setQuests(prev => [...prev, ...content]);
      } else {
        setQuests(content);
        setSelected(new Set());
        setExpanded({});
        setExpandedOpen({});
      }
    } catch (err: any) {
      dispatch(openSnackbar(`Impossible de charger les quêtes : ${err.message}`, "red"));
      if (!append) setQuests([]);
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, [sourceFilter, clientFilter]);

  /* ── Reset + refetch when filters change ── */
  useEffect(() => {
    fetchQuests(0, false);
  }, [fetchQuests]);

  /* ── Toggle tree row ── */
  const toggleExpand = async (quest: Quest) => {
    const id = getId(quest);
    if (!id) return;

    const isOpen = !!expandedOpen[id];

    if (expanded[id] !== undefined) {
      setExpandedOpen(prev => ({ ...prev, [id]: !isOpen }));
      return;
    }

    setCyclesLoading(prev => ({ ...prev, [id]: true }));
    setCyclesError(prev => ({ ...prev, [id]: false }));
    const token = sessionStorage.getItem("jwtToken") ?? "";
    try {
      const res = await fetch(`${URL_LOCATION}/quest/${id}/cycles`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: Quest[] = await res.json();
      setExpanded(prev => ({ ...prev, [id]: Array.isArray(data) ? data : [] }));
      setExpandedOpen(prev => ({ ...prev, [id]: true }));
    } catch {
      setExpanded(prev => ({ ...prev, [id]: [] }));
      setCyclesError(prev => ({ ...prev, [id]: true }));
      setExpandedOpen(prev => ({ ...prev, [id]: true }));
    } finally {
      setCyclesLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  /* ── Publish / Unpublish / Delete ── */
  /* Une seule action de publication (Q8), précédée d'une confirmation qui
     redit le prix engagé (D3). Le serveur refuse un prix à 0 ou une quête
     sans géographie avec un message rédigé pour l'admin : on l'affiche. */
  const doPublish = async (quest: Quest) => {
    const id = getId(quest);
    setPublishing(id);
    dispatch(loaderOn());
    try {
      await publierQuete(id);
      dispatch(openSnackbar(`"${quest.titre}" publiée avec succès.`, "green"));
      fetchQuests(0, false);
    } catch (err: any) {
      dispatch(openSnackbar(messageServeur(err, "Publication impossible."), "red"));
    } finally {
      setPublishing(null);
      dispatch(loaderOff());
    }
  };

  const handlePublish = (quest: Quest) => {
    const id = getId(quest);
    if (!id) { dispatch(openSnackbar("ID de quête introuvable.", "red")); return; }
    setConfirmDialog({
      open: true,
      title: "Valider et publier la quête ?",
      body: texteConfirmationPublication(quest),
      onConfirm: () => { setConfirmDialog(d => ({ ...d, open: false })); doPublish(quest); },
    });
  };

  /* Fin de vie (Q9) : la quête est archivée, ses soumissions et paiements
     restent. C'est la seule issue pour une quête qui a des soumissions. */
  const handleCloturer = (quest: Quest) => {
    const id = getId(quest);
    if (!id) return;
    setConfirmDialog({
      open: true,
      title: "Clôturer la quête ?",
      body: `"${quest.titre}" sera archivée : elle n'est plus proposée aux Spotters, ses soumissions et paiements sont conservés.`,
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        setCloturant(id);
        dispatch(loaderOn());
        try {
          await cloturerQuete(id);
          dispatch(openSnackbar(`"${quest.titre}" clôturée.`, "green"));
          fetchQuests(0, false);
        } catch (err: any) {
          dispatch(openSnackbar(messageServeur(err, "Clôture impossible."), "red"));
        } finally {
          setCloturant(null);
          dispatch(loaderOff());
        }
      },
    });
  };

  const handleUnpublish = async (quest: Quest) => {
    const id = getId(quest);
    if (!id) return;
    setUnpublishing(id);
    dispatch(loaderOn());
    try {
      const res = await fetch(`${URL_LOCATION}/quest/${id}/unpublish`, {
        method: "PUT",
        headers: { ...authHeader(), "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      dispatch(openSnackbar(`"${quest.titre}" dépubliée — statut brouillon.`, "green"));
      fetchQuests(0, false);
    } catch (err: any) {
      dispatch(openSnackbar(`Erreur dépublication : ${err.message}`, "red"));
    } finally {
      setUnpublishing(null);
      dispatch(loaderOff());
    }
  };

  /* Plus de dépublication préalable : le serveur supprime une quête publiée
     sans soumission, et refuse (400, texte affiché) celle qui en a — la
     dépublier d'abord l'aurait laissée en brouillon après le refus. */
  const doDelete = async (quest: Quest) => {
    const id = getId(quest);
    if (!id) return;
    setDeleting(id);
    dispatch(loaderOn());
    try {
      await supprimerQuete(id);
      dispatch(openSnackbar(`"${quest.titre}" supprimée.`, "green"));
      fetchQuests(0, false);
    } catch (err: any) {
      dispatch(openSnackbar(messageServeur(err, "Suppression impossible."), "red"));
    } finally {
      setDeleting(null);
      dispatch(loaderOff());
    }
  };

  const handleDelete = (quest: Quest) => {
    const published = isPublishedStatus(quest.statut);
    setConfirmDialog({
      open: true,
      title: "Supprimer la quête ?",
      body: `${published ? `"${quest.titre}" est actuellement publiée. ` : ""}Êtes-vous sûr de vouloir supprimer "${quest.titre}" ? `
        + "Cette action est irréversible. Une quête qui a des soumissions ne se supprime pas : clôturez-la.",
      onConfirm: () => { setConfirmDialog(d => ({ ...d, open: false })); doDelete(quest); },
    });
  };

  /* ── Bulk delete ── */
  const handleBulkDelete = () => {
    const ids = Array.from(selected);
    const selectedQuests = quests.filter(q => ids.includes(getId(q)));
    setConfirmDialog({
      open: true,
      title: `Supprimer ${ids.length} quête${ids.length > 1 ? "s" : ""} ?`,
      body: `Êtes-vous sûr de vouloir supprimer ces ${ids.length} quête${ids.length > 1 ? "s" : ""} ? Cette action est irréversible. `
        + "Les quêtes qui ont des soumissions sont refusées par le serveur : clôturez-les.",
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        setBulkDeleting(true);
        dispatch(loaderOn());
        let errors = 0;
        let dernierRefus = "";
        for (const quest of selectedQuests) {
          try {
            await supprimerQuete(getId(quest));
          } catch (err: any) {
            errors++;
            dernierRefus = messageServeur(err, "");
          }
        }
        setBulkDeleting(false);
        dispatch(loaderOff());
        if (errors > 0) {
          dispatch(openSnackbar(`${ids.length - errors} supprimée(s), ${errors} refusée(s).${dernierRefus ? ` ${dernierRefus}` : ""}`, "red"));
        } else {
          dispatch(openSnackbar(`${ids.length} quête${ids.length > 1 ? "s" : ""} supprimée${ids.length > 1 ? "s" : ""}.`, "green"));
        }
        fetchQuests(0, false);
      },
    });
  };

  /* ── Selection helpers ── */
  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allIds      = quests.map(getId).filter(Boolean);
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
  const someSelected = allIds.some(id => selected.has(id));

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else             setSelected(new Set(allIds));
  };

  /* ── Counts for segmented control (server-side, so show total from state) ── */
  const loaded = quests.length;

  /* ════════════════════════════════════════ RENDER */
  return (
    <Box sx={{ background: C.bg, minHeight: "100vh", color: C.text, p: { xs: 2, md: 3 } }}>

      {/* ── Page header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <TitrePage domaine="quetes" aide={sourceInitiale === "client_ia" ? AIDE_QUETES.demandesClients : AIDE_QUETES.liste}>{sourceInitiale === "client_ia" ? "Demandes clients" : "Toutes les quêtes"}</TitrePage>
          <Typography sx={{ fontSize: "13px", color: C.sub, mt: 0.5 }}>
            Retrouvez une quête, ses cycles, son statut et son paiement ; publiez-la ou clôturez-la.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", ml: 2, flexShrink: 0 }}>
          {selected.size > 0 && (
            <Button
              startIcon={bulkDeleting
                ? <CircularProgress size={12} sx={{ color: C.error }} />
                : <DeleteOutlineRoundedIcon sx={{ fontSize: "14px !important" }} />}
              disabled={bulkDeleting}
              onClick={handleBulkDelete}
              sx={{
                background: C.errorBg, color: C.error,
                border: `1px solid ${C.errorBdr}`,
                fontSize: "12px", fontWeight: 700,
                textTransform: "none", borderRadius: "8px",
                px: 2, py: "8px", whiteSpace: "nowrap",
                "&:hover": { background: "rgba(162,58,13,0.16)", borderColor: C.error },
                "&.Mui-disabled": { color: C.error, opacity: 0.5 },
              }}
            >
              {bulkDeleting ? "…" : `Supprimer la sélection (${selected.size})`}
            </Button>
          )}
          <Button
            startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
            onClick={() => navigate("/quetes/creer")}
            sx={{
              background: C.accent, color: "#fff", fontWeight: 700, fontSize: "13px",
              textTransform: "none", borderRadius: "8px", px: 2.5, py: "9px",
              whiteSpace: "nowrap",
              boxShadow: "none",
              "&:hover": { background: "#1F1E1B", boxShadow: "none" },
            }}
          >
            Créer une quête
          </Button>
        </Box>
      </Box>

      {/* ── Filters bar ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: "18px", flexWrap: "wrap" }}>

        {/* Source segmented control */}
        <Box sx={{
          display: "inline-flex",
          background: C.card,
          borderRadius: "8px",
          border: "1.5px solid #1F1E1B",
          p: "4px",
        }}>
          {([
            { key: "all"       as const, label: "Toutes",  icon: <ListRoundedIcon          sx={{ fontSize: 15 }} /> },
            { key: "tribe"     as const, label: "Tribe",   icon: <StarRoundedIcon          sx={{ fontSize: 15 }} /> },
            { key: "client_ia" as const, label: "Clients", icon: <PersonOutlineRoundedIcon sx={{ fontSize: 15 }} /> },
          ]).map(({ key, label, icon }) => {
            const active = sourceFilter === key;
            return (
              <Box
                key={key}
                onClick={() => setSourceFilter(key)}
                sx={{
                  display: "flex", alignItems: "center", gap: "6px",
                  cursor: "pointer",
                  px: "20px", py: "10px",
                  borderRadius: "8px",
                  fontSize: "13px", fontWeight: 600, letterSpacing: "0.3px",
                  border: `1px solid ${active ? C.accentBorder : "transparent"}`,
                  background: active ? C.accentBg : "transparent",
                  color: active ? C.accentSoft : C.sub,
                  transition: "all 150ms ease",
                  userSelect: "none",
                  ...(active ? {} : {
                    "&:hover": { color: C.text, background: "rgba(31,30,27,0.04)" },
                  }),
                }}
              >
                {icon}
                {label}
              </Box>
            );
          })}
        </Box>

        {/* Client dropdown */}
        <Select
          value={clientFilter}
          onChange={e => setClientFilter(e.target.value)}
          size="small"
          displayEmpty
          sx={{
            height: "44px",
            background: C.card,
            border: `1px solid ${C.borderInput}`,
            borderRadius: "8px",
            color: clientFilter === "all" ? C.sub : C.text,
            fontSize: "13px",
            fontWeight: 600,
            minWidth: 200,
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "& .MuiSelect-icon": { color: C.sub },
            "& .MuiSelect-select": { py: "10px", px: "14px" },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                background: C.card,
                border: `1px solid ${C.borderInput}`,
                borderRadius: "8px",
                mt: "4px",
                "& .MuiMenuItem-root": {
                  fontSize: "13px", color: C.text, py: "8px", px: "14px",
                  "&:hover":    { background: C.rowHover },
                  "&.Mui-selected": { background: C.accentBg, color: C.accentSoft },
                  "&.Mui-selected:hover": { background: "rgba(31,30,27,0.14)" },
                },
              },
            },
          }}
        >
          <MenuItem value="all">Tous les clients</MenuItem>
          {clientList.map(email => (
            <MenuItem key={email} value={email}>{email}</MenuItem>
          ))}
        </Select>
      </Box>

      {/* ── Table card ── */}
      <Box sx={{
        background: C.card, borderRadius: "8px",
        border: "1.5px solid #1F1E1B", overflow: "hidden",
      }}>

        {/* Table header */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: "40px 1fr 200px 130px 130px 130px 320px",
          px: 2.5, py: "10px",
          borderBottom: `1px solid ${C.border}`,
          background: "rgba(31,30,27,0.02)",
          alignItems: "center",
        }}>
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={toggleSelectAll}
            disabled={quests.length === 0}
            sx={{
              p: 0, color: C.borderInput,
              "&.Mui-checked": { color: C.accentSoft },
              "&.MuiCheckbox-indeterminate": { color: C.accentSoft },
            }}
          />
          {["Titre", "Types", "Statut", "Date", "Paiement", ""].map((h, i) => (
            <Typography key={i} sx={{
              fontSize: "10px", fontWeight: 700, letterSpacing: "0.10em",
              textTransform: "uppercase", color: C.label,
              textAlign: i === 5 ? "right" : "left",
            }}>
              {h}
            </Typography>
          ))}
        </Box>

        {/* Loading */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6, gap: 1.5 }}>
            <CircularProgress size={20} sx={{ color: C.accentSoft }} />
            <Typography sx={{ fontSize: "13px", color: C.sub }}>Chargement…</Typography>
          </Box>
        )}

        {/* Empty */}
        {!loading && quests.length === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 1.5 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: "8px",
              background: C.accentBg, border: `1px solid ${C.accentBorder}`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <ExploreRoundedIcon sx={{ fontSize: 22, color: C.accentSoft }} />
            </Box>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: C.text }}>
              Aucune quête pour l'instant
            </Typography>
            <Typography sx={{ fontSize: "12px", color: C.sub }}>
              Créez votre première quête pour la voir apparaître ici.
            </Typography>
          </Box>
        )}

        {/* Rows */}
        {!loading && quests.map((q, i) => {
          const id             = getId(q);
          const isPublishing_  = publishing   === id;
          const isUnpublishing_= unpublishing === id;
          const isDeleting_    = deleting     === id;
          const isCloturant_   = cloturant    === id;
          const archivee       = estArchivee(q.statut);
          const st             = statusStyle(q.statut);
          const isPublished    = isPublishedStatus(q.statut);
          const isRowSelected  = id ? selected.has(id) : false;
          const isClientIa     = q.source === "client_ia";
          const sansZone       = sansGeographie(q);
          const isParent       = isRecurringParent(q);
          const isOpen         = !!expandedOpen[id];
          const cycles         = expanded[id] ?? [];
          const cycleLoading   = !!cyclesLoading[id];
          const cycleErr       = !!cyclesError[id];
          const showChildren   = isOpen && expanded[id] !== undefined;

          return (
            <React.Fragment key={id || i}>
              {/* ── Parent / standalone row ── */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "40px 1fr 200px 130px 130px 130px 320px",
                  px: 2.5, py: "13px",
                  borderBottom: `1px solid ${C.border}`,
                  background: isRowSelected
                    ? "rgba(31,30,27,0.09)"
                    : i % 2 === 1 ? C.row : "transparent",
                  transition: "background 0.12s ease",
                  alignItems: "center",
                  "&:hover": { background: isRowSelected ? "rgba(31,30,27,0.13)" : C.rowHover },
                }}
              >
                {/* Checkbox */}
                <Box onClick={e => e.stopPropagation()}>
                  <Checkbox
                    size="small"
                    checked={isRowSelected}
                    onChange={() => id && toggleSelect(id)}
                    disabled={!id}
                    sx={{
                      p: 0, color: C.borderInput,
                      "&.Mui-checked": { color: C.accentSoft },
                    }}
                  />
                </Box>

                {/* Titre */}
                <Box sx={{ minWidth: 0, display: "flex", alignItems: "flex-start", gap: "6px" }}>
                  {/* Chevron pour quêtes mères */}
                  {isParent && (
                    <Box
                      onClick={e => { e.stopPropagation(); toggleExpand(q); }}
                      sx={{
                        mt: "1px", flexShrink: 0, cursor: "pointer",
                        color: C.accentSoft, display: "flex", alignItems: "center",
                        "&:hover": { color: C.accent },
                      }}
                    >
                      {cycleLoading
                        ? <CircularProgress size={14} sx={{ color: C.accentSoft }} />
                        : isOpen
                          ? <ExpandMoreIcon  sx={{ fontSize: 18 }} />
                          : <ChevronRightIcon sx={{ fontSize: 18 }} />
                      }
                    </Box>
                  )}

                  <Box
                    sx={{ minWidth: 0, cursor: id ? "pointer" : "default", flex: 1 }}
                    onClick={() => id && navigate(`/quetes/${id}`)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap", overflow: "hidden" }}>
                      <Typography sx={{
                        fontSize: "13px", fontWeight: 600, color: C.text,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {q.titre || "—"}
                      </Typography>
                      {q.challengeMode === true && (
                        <Box sx={{
                          display: "inline-block", ml: 1, flexShrink: 0,
                          background: "rgba(31,30,27,0.15)",
                          border: "1px solid rgba(31,30,27,0.4)",
                          color: "#1F1E1B",
                          borderRadius: "4px", px: 0.75, py: 0.125,
                          fontSize: 10, fontWeight: 700, lineHeight: 1.6,
                        }}>
                          Challenge 🏆
                        </Box>
                      )}
                      {isClientIa && (
                        <Box sx={{
                          display: "inline-flex", alignItems: "center", ml: 1, flexShrink: 0,
                          background: C.accentBg,
                          border: `1px solid ${C.accentBorder}`,
                          boxShadow: "none",
                          color: C.accentSoft,
                          borderRadius: "4px", px: "10px", py: "4px",
                          fontSize: 12, fontWeight: 700, lineHeight: 1.5,
                          whiteSpace: "nowrap",
                        }}>
                          ✦ Soumission client
                        </Box>
                      )}
                      {sansZone && (
                        <Box
                          title="Aucune zone tracée — le géorepérage des soumissions est inactif sur cette quête"
                          sx={{
                            display: "inline-flex", alignItems: "center", ml: 1, flexShrink: 0,
                            background: "rgba(124,86,0,0.09)",
                            border: "1px solid rgba(124,86,0,0.25)",
                            color: "#7C5600",
                            borderRadius: "4px", px: "8px", py: "3px",
                            fontSize: 11, fontWeight: 700, lineHeight: 1.5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          ⚠ Sans géographie
                        </Box>
                      )}
                      {isParent && (
                        <Box sx={{
                          display: "inline-block", ml: 1, flexShrink: 0,
                          background: "rgba(6,182,212,0.09)",
                          border: "1px solid rgba(6,182,212,0.25)",
                          color: "#3D7F78",
                          borderRadius: "4px", px: 0.75, py: 0.125,
                          fontSize: 10, fontWeight: 700, lineHeight: 1.6,
                        }}>
                          ↻ {q.frequenceCycle}
                        </Box>
                      )}
                    </Box>
                    {id && (
                      <Typography sx={{ fontSize: "10px", color: C.muted, mt: "2px", fontFamily: "monospace" }}>
                        {id.slice(-8)}
                      </Typography>
                    )}
                    {isClientIa && q.createdBy && (
                      <Typography sx={{ fontSize: "11px", color: C.sub, mt: "2px" }}>
                        {q.createdBy}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Types */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {(q.types ?? []).slice(0, 2).map(t => (
                    <Chip
                      key={t}
                      label={`${QUEST_TYPE_EMOJIS[t] ?? ""} ${QUEST_TYPE_LABELS[t] ?? t}`}
                      size="small"
                      sx={{
                        height: 20, fontSize: "10px", fontWeight: 600,
                        background: C.accentBg, color: C.accentSoft,
                        border: `1px solid ${C.accentBorder}`,
                        borderRadius: "5px",
                        "& .MuiChip-label": { px: "6px" },
                      }}
                    />
                  ))}
                  {(q.types ?? []).length > 2 && (
                    <Chip
                      label={`+${q.types.length - 2}`}
                      size="small"
                      sx={{
                        height: 20, fontSize: "10px",
                        background: "rgba(31,30,27,0.05)", color: C.sub,
                        border: `1px solid ${C.borderInput}`,
                        borderRadius: "5px",
                        "& .MuiChip-label": { px: "6px" },
                      }}
                    />
                  )}
                </Box>

                {/* Statut */}
                <Box>
                  <Box sx={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    background: st.bg, border: `1px solid ${st.border}`,
                    borderRadius: "5px", px: "8px", py: "3px",
                  }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: st.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: st.color, lineHeight: 1 }}>
                      {st.label}
                    </Typography>
                  </Box>
                </Box>

                {/* Date création */}
                <Typography sx={{ fontSize: "12px", color: C.sub }}>
                  {fmtDate(q.createdAt ?? q.dateDebut)}
                </Typography>

                {/* Paiement */}
                {(() => {
                  const ps = paiementStyle(q.statutPaiement);
                  return (
                    <Box sx={{
                      display: "inline-flex", alignItems: "center", gap: "5px",
                      background: ps.bg, border: `1px solid ${ps.border}`,
                      borderRadius: "5px", px: "8px", py: "3px",
                    }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: ps.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: ps.color, lineHeight: 1 }}>
                        {ps.label}
                      </Typography>
                    </Box>
                  );
                })()}

                {/* Actions */}
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: "6px" }}
                  onClick={e => e.stopPropagation()}
                >
                  {!isPublished && !archivee && (
                    <Button
                      size="small"
                      startIcon={isPublishing_
                        ? <CircularProgress size={12} sx={{ color: C.success }} />
                        : <PublishRoundedIcon sx={{ fontSize: "13px !important" }} />}
                      disabled={isPublishing_}
                      onClick={() => handlePublish(q)}
                      sx={{
                        background: C.successBg, color: C.success,
                        border: `1px solid ${C.successBdr}`,
                        fontSize: "11px", fontWeight: 700,
                        textTransform: "none", borderRadius: "6px", px: 1.5, py: "5px",
                        "&:hover": { background: "rgba(53,106,15,0.16)", borderColor: C.success },
                        "&.Mui-disabled": { color: C.success, opacity: 0.5 },
                      }}
                    >
                      {isPublishing_ ? "…" : "Valider et publier"}
                    </Button>
                  )}
                  {isPublished && (
                    <Button
                      size="small"
                      startIcon={isUnpublishing_
                        ? <CircularProgress size={12} sx={{ color: C.gold }} />
                        : <UnpublishedRoundedIcon sx={{ fontSize: "13px !important" }} />}
                      disabled={isUnpublishing_}
                      onClick={() => handleUnpublish(q)}
                      sx={{
                        background: C.goldBg, color: C.gold,
                        border: `1px solid ${C.goldBdr}`,
                        fontSize: "11px", fontWeight: 700,
                        textTransform: "none", borderRadius: "6px", px: 1.5, py: "5px",
                        "&:hover": { background: "rgba(124,86,0,0.18)", borderColor: C.gold },
                        "&.Mui-disabled": { color: C.gold, opacity: 0.5 },
                      }}
                    >
                      {isUnpublishing_ ? "…" : "Dépublier"}
                    </Button>
                  )}
                  {!archivee && (
                    <Button
                      size="small"
                      startIcon={isCloturant_
                        ? <CircularProgress size={12} sx={{ color: C.label }} />
                        : <ArchiveOutlinedIcon sx={{ fontSize: "13px !important" }} />}
                      disabled={isCloturant_}
                      onClick={() => handleCloturer(q)}
                      sx={{
                        background: "rgba(31,30,27,0.04)", color: C.label,
                        border: `1px solid ${C.borderInput}`,
                        fontSize: "11px", fontWeight: 700,
                        textTransform: "none", borderRadius: "6px", px: 1.5, py: "5px",
                        "&:hover": { background: "rgba(31,30,27,0.08)", borderColor: C.label },
                        "&.Mui-disabled": { color: C.label, opacity: 0.5 },
                      }}
                    >
                      {isCloturant_ ? "…" : "Clôturer"}
                    </Button>
                  )}
                  <Button
                    size="small"
                    startIcon={isDeleting_
                      ? <CircularProgress size={12} sx={{ color: C.error }} />
                      : <DeleteOutlineRoundedIcon sx={{ fontSize: "13px !important" }} />}
                    disabled={isDeleting_}
                    onClick={() => handleDelete(q)}
                    sx={{
                      background: C.errorBg, color: C.error,
                      border: `1px solid ${C.errorBdr}`,
                      fontSize: "11px", fontWeight: 700,
                      textTransform: "none", borderRadius: "6px", px: 1.5, py: "5px",
                      "&:hover": { background: "rgba(162,58,13,0.16)", borderColor: C.error },
                      "&.Mui-disabled": { color: C.error, opacity: 0.5 },
                    }}
                  >
                    {isDeleting_ ? "…" : "Supprimer"}
                  </Button>
                </Box>
              </Box>

              {/* ── Child cycle rows ── */}
              {showChildren && (
                <>
                  {cycleErr && cycles.length === 0 && (
                    <Box sx={{
                      pl: "72px", py: "10px", pr: 2.5,
                      borderBottom: `1px solid ${C.border}`,
                      background: C.childRow,
                    }}>
                      <Typography sx={{ fontSize: "12px", color: C.muted, fontStyle: "italic" }}>
                        Aucun cycle généré
                      </Typography>
                    </Box>
                  )}
                  {!cycleErr && cycles.length === 0 && (
                    <Box sx={{
                      pl: "72px", py: "10px", pr: 2.5,
                      borderBottom: `1px solid ${C.border}`,
                      background: C.childRow,
                    }}>
                      <Typography sx={{ fontSize: "12px", color: C.muted, fontStyle: "italic" }}>
                        Aucun cycle généré
                      </Typography>
                    </Box>
                  )}
                  {cycles.map((child, ci) => {
                    const childId = getId(child);
                    const cst     = statusStyle(child.statut);
                    return (
                      <Box
                        key={childId || `child-${ci}`}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "40px 1fr 200px 130px 130px 130px 320px",
                          pl: "72px", pr: 2.5, py: "11px",
                          borderBottom: `1px solid ${C.border}`,
                          background: C.childRow,
                          alignItems: "center",
                          "&:hover": { background: "rgba(31,30,27,0.03)" },
                        }}
                      >
                        {/* No checkbox for children — spans first col via pl offset */}
                        <Box /> {/* empty checkbox cell */}

                        {/* Titre enfant */}
                        <Box
                          sx={{ minWidth: 0, cursor: childId ? "pointer" : "default" }}
                          onClick={() => childId && navigate(`/quetes/${childId}`)}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden" }}>
                            <Box sx={{
                              display: "inline-block", flexShrink: 0,
                              background: "rgba(6,182,212,0.09)",
                              border: "1px solid rgba(6,182,212,0.25)",
                              color: "#3D7F78",
                              borderRadius: "4px", px: 0.75, py: 0.125,
                              fontSize: 10, fontWeight: 700, lineHeight: 1.6, mr: 0.5,
                            }}>
                              Cycle
                            </Box>
                            <Typography sx={{
                              fontSize: "12px", fontWeight: 500, color: C.text,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {child.titre || "—"}
                            </Typography>
                          </Box>
                          {childId && (
                            <Typography sx={{ fontSize: "10px", color: C.muted, mt: "2px", fontFamily: "monospace" }}>
                              {childId.slice(-8)}
                            </Typography>
                          )}
                        </Box>

                        {/* Types */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {(child.types ?? []).slice(0, 2).map(t => (
                            <Chip
                              key={t}
                              label={`${QUEST_TYPE_EMOJIS[t] ?? ""} ${QUEST_TYPE_LABELS[t] ?? t}`}
                              size="small"
                              sx={{
                                height: 20, fontSize: "10px", fontWeight: 600,
                                background: C.accentBg, color: C.accentSoft,
                                border: `1px solid ${C.accentBorder}`,
                                borderRadius: "5px",
                                "& .MuiChip-label": { px: "6px" },
                              }}
                            />
                          ))}
                        </Box>

                        {/* Statut */}
                        <Box>
                          <Box sx={{
                            display: "inline-flex", alignItems: "center", gap: "5px",
                            background: cst.bg, border: `1px solid ${cst.border}`,
                            borderRadius: "5px", px: "8px", py: "3px",
                          }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: cst.color, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: cst.color, lineHeight: 1 }}>
                              {cst.label}
                            </Typography>
                          </Box>
                        </Box>

                        {/* dateDebut (plus parlant pour les cycles) */}
                        <Typography sx={{ fontSize: "12px", color: C.sub }}>
                          {fmtDate(child.dateDebut)}
                        </Typography>

                        {/* Pas de paiement ni d'actions sur les cycles */}
                        <Box />
                        <Box />
                      </Box>
                    );
                  })}
                </>
              )}
            </React.Fragment>
          );
        })}

        {/* Load more footer */}
        {!loading && loaded > 0 && (
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 2.5, py: "12px",
            borderTop: `1px solid ${C.border}`,
          }}>
            <Typography sx={{ fontSize: "12px", color: C.muted }}>
              {selected.size > 0 && `${selected.size} sélectionnée${selected.size > 1 ? "s" : ""} · `}
              {loaded} sur {totalElements} quête{totalElements > 1 ? "s" : ""}
            </Typography>
            {loaded < totalElements && (
              <Button
                onClick={() => fetchQuests(currentPage + 1, true)}
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={12} sx={{ color: C.accentSoft }} /> : undefined}
                sx={{
                  background: C.accentBg, color: C.accentSoft,
                  border: `1px solid ${C.accentBorder}`,
                  fontSize: "12px", fontWeight: 700,
                  textTransform: "none", borderRadius: "8px",
                  px: 2, py: "6px",
                  "&:hover": { background: "rgba(31,30,27,0.16)", borderColor: C.accent },
                  "&.Mui-disabled": { color: C.accentSoft, opacity: 0.5 },
                }}
              >
                {loadingMore ? "Chargement…" : `Charger plus (${totalElements - loaded} restantes)`}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* ── Confirmation dialog ── */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(d => ({ ...d, open: false }))}
        PaperProps={{
          sx: {
            background: "var(--c-surface)", border: "1.5px solid #1F1E1B",
            borderRadius: "8px", color: C.text, minWidth: 360,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 700, color: C.text, pb: 1 }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "13px", color: C.sub }}>
            {confirmDialog.body}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmDialog(d => ({ ...d, open: false }))}
            sx={{
              color: C.sub, fontSize: "12px", fontWeight: 600, textTransform: "none",
              borderRadius: "6px", px: 2,
              "&:hover": { background: "rgba(31,30,27,0.06)" },
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={confirmDialog.onConfirm}
            sx={{
              background: C.errorBg, color: C.error,
              border: `1px solid ${C.errorBdr}`,
              fontSize: "12px", fontWeight: 700,
              textTransform: "none", borderRadius: "6px", px: 2,
              "&:hover": { background: "rgba(162,58,13,0.16)", borderColor: C.error },
            }}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
